import { defineComponent as _defineComponent } from 'vue'
import Modal from "@/components/ui/Modal.vue";
  import CheckboxInput from "@/components/ui/input/CheckboxInput.vue";
  import { useStore } from "vuex";
  import { ref, watch } from "vue";
  import plural from "@/features/plural";
  import { StageInterface } from "@/types/StageInterface";
  import { useToast } from "vue-toastification";

  type StageWithDocuments = StageInterface & {
    stageDocuments?: { fileName?: string }[];
  };

  
export default /*@__PURE__*/_defineComponent({
  __name: 'ShareStageModal',
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    fileData: {
      type: Object,
      default: () => ({}),
    },
    estateId: {
      type: String,
      required: true,
    },
  },
  emits: ["close"],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

  const toast = useToast();
  const store = useStore();
  const emits = __emit;

  const props = __props;

  const loadingStages = ref(false);
  const stages = ref<StageWithDocuments[]>([]);
  const initialStageIds = ref(new Set<string>());
  const checkedStageIds = ref(new Set<string>());
  const stagesRequestId = ref(0);

  watch(
    () => [props.isOpen, props.fileData.fileName],
    ([isOpen, fileName], [wasOpen, previousFileName]) => {
      if (isOpen && (!wasOpen || fileName !== previousFileName)) {
        loadStages();
      } else {
        resetSelections();
      }
    }
  );

  const loadStages = async () => {
    const requestId = ++stagesRequestId.value;
    const fileName = props.fileData.fileName;

    resetSelections(false);
    loadingStages.value = true;

    try {
      const res = await store.dispatch("stage/getStages", {
        estateId: props.estateId,
      });

      if (requestId !== stagesRequestId.value || !props.isOpen) {
        return;
      }

      stages.value = res.data ?? [];
      const preChecked = new Set<string>();

      stages.value.forEach((stage) => {
        const docs = stage.stageDocuments ?? [];

        if (docs.some((document) => document.fileName === fileName)) {
          preChecked.add(stage._id);
        }
      });
      initialStageIds.value = new Set(preChecked);
      checkedStageIds.value = new Set(preChecked);
    } finally {
      if (requestId === stagesRequestId.value) {
        loadingStages.value = false;
      }
    }
  };

  const toggleStage = (stageId: string, checked: boolean) => {
    const next = new Set(checkedStageIds.value);

    if (checked) {
      next.add(stageId);
    } else {
      next.delete(stageId);
    }

    checkedStageIds.value = next;
  };

  const send = async () => {
    const toAdd = [...checkedStageIds.value].filter(
      (id) => !initialStageIds.value.has(id)
    );
    const toRemove = [...initialStageIds.value].filter(
      (id) => !checkedStageIds.value.has(id)
    );

    const addPromises = toAdd.map((stageId) =>
      store.dispatch("stage/shareDocumentWithStage", {
        stageId,
        fileData: props.fileData,
      })
    );
    const removePromises = toRemove.map((stageId) =>
      store.dispatch("stage/removeDocumentFromStage", {
        stageId,
        fileData: { fileName: props.fileData.fileName },
      })
    );

    try {
      await Promise.all([...addPromises, ...removePromises]);
      toast.success("Stage document sharing updated successfully");
    } catch {
      toast.error("Something went wrong");
    } finally {
      close();
    }
  };

  const close = () => {
    stages.value = [];
    resetSelections();
    emits("close");
  };

  const resetSelections = (invalidateRequest = true) => {
    if (invalidateRequest) {
      stagesRequestId.value += 1;
    }

    stages.value = [];
    loadingStages.value = false;
    initialStageIds.value = new Set();
    checkedStageIds.value = new Set();
  };

const __returned__ = { toast, store, emits, props, loadingStages, stages, initialStageIds, checkedStageIds, stagesRequestId, loadStages, toggleStage, send, close, resetSelections, Modal, CheckboxInput, get useStore() { return useStore }, ref, watch, get plural() { return plural }, get StageInterface() { return StageInterface }, get useToast() { return useToast } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})